import React from 'react';
import {
  Inline,
  MediaObject,
  Stack,
  useViewport,
  IconPin,
  IconTime,
  Viewport,
  useDevice,
} from '@treatwell/ui';

import { CmsCommon } from 'js/model/cms/cms-common';
import { CmsVenue } from 'js/model/cms/cms-venue';
import { createVenueTags } from 'js/model/view/venue-tags';
import { CmsVenuePage } from 'js/model/cms/cms-venue-page';
import { SalesPriceDiscountType } from 'js/model/rainbow/SalesPriceDiscountType';
import { VenueTag, VenueTagType } from 'js/components/VenueTag';
import { IconWithContent } from 'js/components/IconWithContent';
import { getVenueRelativeOpeningHours } from 'js/helpers/venue-relative-opening-time';

import { VenueOutput } from 'js/model/rainbow/venue/VenueOutput';
import { FormattedAddress } from './Address';
import styles from './style.module.css';

interface Props {
  cmsCommon: CmsCommon;
  cmsVenue: CmsVenue;
  cmsChainTemplate: CmsVenuePage['page']['venue']['details']['chain'];
  countryCode: string;
  isActiveVenue?: boolean;
  venue: VenueOutput;
}

export function Details2(props: Props): React.ReactElement {
  const { isMobile: isMobileDevice } = useDevice();
  const isMobile = useViewport({
    device: 'mobile',
    serverRender: isMobileDevice,
  });

  const discountTypes = props.venue.priceRange
    ? props.venue.priceRange.yieldDiscountTypes
    : [];

  const venueTagsData = createVenueTags(
    {
      jit: discountTypes.includes(SalesPriceDiscountType.Jit),
      offpeak: discountTypes.includes(SalesPriceDiscountType.Offpeak),
    },
    props.venue.type,
    props.venue.subTypes,
    props.cmsVenue
  );

  const venueTypesTagData = venueTagsData.filter(
    (tag) =>
      tag.type === VenueTagType.HomeBased ||
      tag.type === VenueTagType.MobileVenue
  );

  const discountsTagData = venueTagsData.find(
    (tag) => tag.type === VenueTagType.YieldDiscount
  );

  function venueTypes(): React.ReactElement | null {
    if (!props.isActiveVenue) {
      return null;
    }

    if (venueTypesTagData.length === 0) {
      return null;
    }

    const venueTags = venueTypesTagData.map((tagData) => (
      <VenueTag key={tagData.label} size="small" {...tagData} />
    ));

    return (
      <>
        <Viewport serverRender={isMobileDevice} device="mobile">
          <Stack space="xxs">{venueTags}</Stack>
        </Viewport>

        <Viewport serverRender={!isMobileDevice} device={['tablet', 'desktop']}>
          <Inline space="lg">{venueTags}</Inline>
        </Viewport>
      </>
    );
  }

  function discounts(): React.ReactElement | null {
    if (!props.isActiveVenue) {
      return null;
    }

    if (!discountsTagData) {
      return null;
    }

    return <VenueTag {...discountsTagData} />;
  }

  const openingHours = getVenueRelativeOpeningHours(
    props.venue.openingHours,
    props.countryCode,
    props.cmsCommon
  );

  return (
    <Stack space={isMobile ? 'xs' : 'md'}>
      <MediaObject align="center" space={isMobile ? 'xs' : 'md'}>
        <IconPin size={isMobile ? 16 : 24} />
        <Stack space="xxs" className={styles.location}>
          <FormattedAddress
            address={props.venue.location.address}
            chain={props.venue.chain}
            cmsChainTemplate={props.cmsChainTemplate}
            venue={props.venue}
            countryCode={props.countryCode}
          />
          {venueTypes()}
        </Stack>
      </MediaObject>

      {props.isActiveVenue && (
        <IconWithContent icon={IconTime} text={openingHours} />
      )}

      {discounts()}
    </Stack>
  );
}
